import React, { useMemo, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { useProductsBranch } from "../../hooks/useProductsBranch";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";

const N = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);
const fmt = (n) =>
  N(n).toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function ProductsTablePage() {
  const { products, loading } = useProductsBranch();

  const [query, setQuery] = useState("");
  const [orderBy, setOrderBy] = useState("itemCode");
  const [order, setOrder] = useState("asc");
  // const [page, setPage] = useState(0);
  // const [rowsPerPage, setRowsPerPage] = useState(25);

  // เตรียมข้อมูล
  const rows = useMemo(() => {
    return (
      (products || [])
        .map((p) => ({
          id: p.id ?? p.productId ?? "-",
          itemCode: p.product?.code ?? p.code ?? p.history?.itemCode ?? "-",
          title: p.product?.title ?? p.title ?? p.name ?? "-",
          categoryName: p.product?.category?.name ?? p.category?.name ?? "-",
          subCategoryName:
            p.product?.subCategory?.name ?? p.subCategory?.name ?? "-",
          minWeight: N(p.minWeight, 0),
          unitName: p.unit?.name ?? p.unitName ?? "กก.",
          basePrice: N(p.finalPrice ?? p.price ?? p.basePrice, 0),
          groupPrices: Array.isArray(p.groupPrices) ? p.groupPrices : [],
        }))
        .sort((a, b) =>
          a.itemCode.localeCompare(b.itemCode, "en", { numeric: true })
        )
    );
  }, [products]);

  // ค้นหา
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        String(r.id).includes(q) ||
        (r.itemCode ?? "").toLowerCase().includes(q) ||
        (r.title ?? "").toLowerCase().includes(q) ||
        (r.categoryName ?? "").toLowerCase().includes(q) ||
        (r.subCategoryName ?? "").toLowerCase().includes(q)
    );
  }, [rows, query]);

  // เรียง
  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = a[orderBy];
      const bv = b[orderBy];
      if (typeof av === "number" && typeof bv === "number") {
        return order === "asc" ? av - bv : bv - av;
      }
      const sa = String(av ?? "");
      const sb = String(bv ?? "");
      const cmp = sa.localeCompare(sb, "en", { numeric: true });
      return order === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [filtered, order, orderBy]);

  // const paged = useMemo(() => {
  //   const start = page * rowsPerPage;
  //   return sorted.slice(start, start + rowsPerPage);
  // }, [sorted, page, rowsPerPage]);

  const handleRequestSort = (key) => {
    if (orderBy === key) setOrder(order === "asc" ? "desc" : "asc");
    else {
      setOrderBy(key);
      setOrder("asc");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 font-sans">
      <div className="max-w-[95%] mx-auto">
        <div className="mb-6">
          <AppBreadcrumbs />
        </div>

        {/* Header & Controls */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <span className="w-1 h-8 bg-blue-600 rounded-full"></span>
              รายละเอียดราคารับซื้อ
              <span className="text-sm font-medium px-3 py-1 bg-blue-50 text-blue-600 rounded-full border border-blue-100">
                {rows.length.toLocaleString("th-TH")} รายการ
              </span>
            </h1>

            <div className="w-full md:w-auto relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="ค้นหารหัส สินค้า หมวด หรือหมวดย่อย"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  // setPage(0);
                }}
                className="w-full md:w-80 pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
              />
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-100">
                  <tr>
                    <th
                      onClick={() => handleRequestSort("itemCode")}
                      className="px-4 py-4 cursor-pointer hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-1">
                        รหัส{" "}
                        {orderBy === "itemCode" && (
                          <span className="text-blue-600">
                            {order === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleRequestSort("title")}
                      className="px-4 py-4 cursor-pointer hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-1">
                        สินค้า{" "}
                        {orderBy === "title" && (
                          <span className="text-blue-600">
                            {order === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleRequestSort("categoryName")}
                      className="px-4 py-4 cursor-pointer hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-1">
                        หมวดหมู่หลัก{" "}
                        {orderBy === "categoryName" && (
                          <span className="text-blue-600">
                            {order === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleRequestSort("subCategoryName")}
                      className="px-4 py-4 cursor-pointer hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-1">
                        หมวดหมู่ย่อย{" "}
                        {orderBy === "subCategoryName" && (
                          <span className="text-blue-600">
                            {order === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-4 text-right">น้ำหนักขั้นต่ำ</th>
                    <th className="px-4 py-4">หน่วย</th>
                    <th className="px-4 py-4 text-right w-64">ราคาซื้อ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {sorted.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                        ไม่พบข้อมูล
                      </td>
                    </tr>
                  ) : (
                    sorted.map((r) => (
                      <tr
                        key={`${r.id}-${r.title}`}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-3 text-gray-600">{r.itemCode}</td>
                        <td className="px-4 py-3 font-medium text-gray-800">
                          {r.title}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{r.categoryName}</td>
                        <td className="px-4 py-3 text-gray-600">
                          {r.subCategoryName}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600">
                          {fmt(r.minWeight)}
                        </td>
                        <td className="px-4 py-3 text-gray-500">{r.unitName}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex flex-col items-end gap-1">
                            <span className="font-bold text-gray-800 text-lg">
                              {fmt(r.basePrice)}
                            </span>
                            {r.groupPrices.length > 0 && (
                                <div className="flex flex-col items-end gap-0.5 mt-1">
                                    {r.groupPrices.map((g) => (
                                        <span key={g.customerGroupId} className="text-xs text-gray-500">
                                            กลุ่ม {g.customerGroupName} <span className="font-bold text-gray-700 ml-1">{fmt(g.finalPrice)}</span>
                                        </span>
                                    ))}
                                </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
